import { defineComponent, ref, onMounted, watch } from 'vue'
import { RouterLink, useRouter, useRoute } from 'vue-router'
import { useSmartBack } from '@/composables/use-smart-back'
import {
  NavBar as VanNavBar,
  Icon as VanIcon,
  Sticky as VanSticky,
  Search as VanSearch,
  Popover as VanPopover,
  Popup as VanPopup,
  Picker as VanPicker,
  IndexBar as VanIndexBar,
  IndexAnchor as VanIndexAnchor,
  Empty as VanEmpty,
  showLoadingToast,
  closeToast,
} from 'vant'
import { useRegionStore } from '@/stores/region-store'
import LotterySwitcher from '@/components/lottery-switcher'
import { renderGalleryColorMenu } from '@/components/gallery-waterfall'
import type { PictureTypeLetterGroup } from '@/api/types.gen'
import {
  getApiV1TukuPictureTypesByRegion,
  getApiV1TukuPictureTypesByRegionYears,
} from '@/api/sdk.gen'

type ColorValue = 1 | 2
const COLOR_MAP: Record<ColorValue, string> = { 1: '彩色', 2: '黑白' }

const CURRENT_YEAR = new Date().getFullYear()

export default defineComponent({
  name: 'GallerySearchPage',
  setup() {
    const router = useRouter()
  const goBack = useSmartBack()
    const route = useRoute()
    const regionStore = useRegionStore()

    const qRegion = route.query.region
    if (qRegion === 'hk' || qRegion === 'aocai') {
      regionStore.setRegion(qRegion)
    }

    const value = ref('')
    const year = ref(CURRENT_YEAR)
    const color = ref<ColorValue>(1)
    const groups = ref<PictureTypeLetterGroup[]>([])
    const isNoData = ref(false)
    const yearColumns = ref<Array<{ text: string; value: string }>>([])
    const showYear = ref(false)
    const showColor = ref(false)

    async function fetchGroups() {
      const toast = showLoadingToast({ duration: 0, message: '加载中...', forbidClick: true })
      try {
        const res = await getApiV1TukuPictureTypesByRegion({
          path: { region: regionStore.region },
          query: {
            group_by: 'letter',
            year: year.value,
            color: String(color.value),
            keyword: value.value || undefined,
          },
        })
        const list = res.data?.data?.list_by_letter ?? []
        groups.value = list.filter((g) => (g.items?.length ?? 0) > 0)
        isNoData.value = groups.value.length === 0
      } finally {
        closeToast(toast as any)
      }
    }

    async function fetchYearList() {
      const res = await getApiV1TukuPictureTypesByRegionYears({
        path: { region: regionStore.region },
      })
      const list = res.data?.data?.list ?? []
      yearColumns.value = list.map((y) => ({ text: String(y), value: String(y) }))
    }

    onMounted(() => {
      const qYear = Number(route.query.year)
      const qColorRaw = route.query.color
      if (qYear > 2000 && qYear <= CURRENT_YEAR) year.value = qYear
      if (qColorRaw === 'color' || qColorRaw === '1' || Number(qColorRaw) === 1) {
        color.value = 1
      } else if (qColorRaw === 'bw' || qColorRaw === '2' || Number(qColorRaw) === 2) {
        color.value = 2
      }
      fetchYearList()
      fetchGroups()
    })

    watch(
      () => regionStore.region,
      () => {
        router.replace({ query: { ...route.query, region: regionStore.region } })
        fetchYearList()
        fetchGroups()
      },
    )

    function onSearch() {
      fetchGroups()
    }

    function onSelectColor(c: ColorValue) {
      color.value = c
      showColor.value = false
      fetchGroups()
    }

    function onConfirmYear({ selectedValues }: { selectedValues: string[] }) {
      showYear.value = false
      const y = Number(selectedValues[0])
      if (y && y !== year.value) {
        year.value = y
        fetchGroups()
      }
    }


    const indexList = () => groups.value.map((g) => g.letter ?? '').filter(Boolean)

    /** 色彩下拉菜单：彩色 / 黑白 */
    const renderColorMenu = () => renderGalleryColorMenu(color.value, onSelectColor)

    /** 按首字母分组的一段：索引锚点 + 图库条目 */
    const renderGroup = (g: PictureTypeLetterGroup) => (
      <div key={g.letter}>
        <VanIndexAnchor index={g.letter ?? ''}>
          <span class="search-list-title">
            <b>{g.letter}</b>
          </span>
        </VanIndexAnchor>
        {(g.items ?? []).map((item) => (
          // 不加 btn-reset：生产包里 vant 样式先于 uno 加载，btn-reset 会盖掉 .van-cell 的内边距与颜色
          <RouterLink
            key={item.pictureTypeId}
            class="van-cell no-underline"
            to={`/gallery/${item.pictureTypeId}?year=${year.value}&region=${regionStore.region}`}
          >
            {item.pictureName}
            <span class="number">{item.periodStr}</span>
          </RouterLink>
        ))}
      </div>
    )

    return () => (
      <div class="search-home">
        <VanSticky>
          <div class="sticky-body">
            <VanNavBar title="六合图库" onClickLeft={() => goBack()}>
              {{
                left: () => <VanIcon name="arrow-left" color="#333" size="16" />,
                right: () => (
                  <div style="display:flex;align-items:center;gap:10px">
                    <button
                      type="button"
                      class="nav-icon-text btn-reset"
                      onClick={() => (showYear.value = true)}
                    >
                      <span class="icon block">
                        <img src="/search-year.png" style="height:20px" alt="" />
                      </span>
                      <span class="text block">年份</span>
                    </button>
                    <LotterySwitcher />
                  </div>
                ),
              }}
            </VanNavBar>
            <div class="search-hd">
              <VanSearch
                v-model={value.value}
                placeholder="搜索"
                showAction
                shape="round"
                background="#fff"
                onSearch={onSearch}
              >
                {{
                  'left-icon': () => (
                    <VanPopover
                      v-model:show={showColor.value}
                      placement="bottom-start"
                      offset={[0, 5]}
                    >
                      {{
                        reference: () => (
                          <div class="left-icon">
                            {COLOR_MAP[color.value]} ▾
                          </div>
                        ),
                        default: renderColorMenu,
                      }}
                    </VanPopover>
                  ),
                  action: () => (
                    <button type="button" class="search-btn btn-reset block" onClick={onSearch}>
                      搜索
                    </button>
                  ),
                }}
              </VanSearch>
            </div>
          </div>
        </VanSticky>

        <div class="search-list">
          {groups.value.length > 0 ? (
            <VanIndexBar
              indexList={indexList()}
              sticky={false}
              highlightColor="#333"
              style={{
                color: '#333',
                '--van-index-bar-index-font-size': '10px',
                '--van-index-bar-index-line-height': '14px',
                '--van-font-bold': '500',
              }}
            >
              {groups.value.map(renderGroup)}
            </VanIndexBar>
          ) : (
            <VanEmpty description={isNoData.value ? '没有找到您想要的' : '暂无数据'} />
          )}
        </div>

        <VanPopup v-model:show={showYear.value} position="bottom">
          <VanPicker
            title="选择年份"
            columns={yearColumns.value}
            onConfirm={onConfirmYear}
            onCancel={() => (showYear.value = false)}
          />
        </VanPopup>
      </div>
    )
  },
})
